import React from "react";
import { View } from "react-native";
import { injectIntl } from "react-intl";
import styled from "styled-components/native";
import { AutoSizer, List, InfiniteLoader } from "react-virtualized";
import {
  messages,
  NoDataText,
  NoRows,
  RowWrapper,
  LoadingText,
  defaultRowRenderer
} from "./common";

const Wrapper = styled.View`
  flex: 1;
`;

/**
 * Component uses the infinite loader to get data from a source and virtualizes it (only renders nodes being seen by the user).
 *
 * This is a highly optimized way of rendering a large number of records in a list format.
 *
 * Props are:
 * - rows: Array of objects that will be shown in the UI.
 * - totalRows: Total number of rows in the server without the paging. For example, there may be 5000 rows (totalRows) in the database but we only show 10 at a time.
 * - loadMoreRows: Function that will be called when the user is approaching the end of the list (via scrolling) and more rows are needed from the database.
 * - loading: Indicates if the data is currently being loaded from the server.
 * - refetch: Function called when the data needs to be obtained from the database again (refresh).
 * - fixedHeader: Component that will be shown before the rows, usually used to display a toolbar.
 * - noRowsComponent: Component that must be shown when there are no rows (an empty database for example).
 * - rowRenderer: Function that must be called for every row so that the UI is painted with the row data. This function includes the "Loading" message.
 * _ recordRenderer: Function that must render the row data, this is only called when the row data has been loaded.
 * - fixedFooter: Function Component that will be shown after the rows, can be used to show a summary or total.
 * - style: Object containing any style that must be applied to the outer container that holds the header, list and footer.
 * - contentStyle: Object containing any style that must be applied to the content of the virtualized list.
 * - rowHeight: A function that must return the exact height of each row, defaults to return 70 pixels. This is used by the virtualization library we use to do infinite scrolling in a web browser.
 */
const InfiniteList = ({
  intl,
  rows,
  totalRows,
  loadMoreRows,
  loading,
  refetch,
  fixedHeader,
  noRowsComponent: NoRowsComponent,
  rowRenderer,
  recordRenderer,
  fixedFooter,
  style,
  contentStyle,
  horizontalMargins,
  rowHeight
}) => (
  <View style={style}>
    {fixedHeader}

    {/* Double wrapper is done so that AutoSizer can correctly calculate the height without the header and footer (it uses the parent container to do its thing) */}
    <Wrapper>
      <InfiniteLoader
        isRowLoaded={({ index }) => index < rows.length}
        loadMoreRows={(startIndex, stopIndex) =>
          !loading && loadMoreRows(startIndex, stopIndex)
        }
        rowCount={totalRows}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }) => (
              <List
                onRowsRendered={onRowsRendered}
                noRowsRenderer={props => <NoRowsComponent {...props} />}
                ref={registerChild}
                rowCount={totalRows}
                rowHeight={({ index }) => rowHeight({ index, rows })}
                rowRenderer={({ style, ...restRowProps }) => rowRenderer({
                  ...restRowProps,
                  intl,
                  recordRenderer,

                  // Style mangling with margins is done so that the scroll bar is far to the right instead of being right up against the row item
                  style: {
                    ...style,
                    width: width - (horizontalMargins * 2),
                    marginLeft: horizontalMargins,
                    marginRight: horizontalMargins
                  },

                  // VirtualizedList has this, but react-virtualized doesn't so we simulate it here for consistency
                  item: (restRowProps.index < rows.length) && rows[restRowProps.index]
                }, rows)}
                style={contentStyle}
                width={width}
                height={height}
              />
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </Wrapper>

    {fixedFooter(rows)}
  </View>
);

InfiniteList.defaultProps = {
  totalRows: 0,
  noRowsComponent: NoRows,
  rowRenderer: defaultRowRenderer,
  horizontalMargins: 0,
  rowHeight: () => 70,
  fixedFooter: () => { }
};

export default injectIntl(InfiniteList);
